import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useQuery, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { EditModal } from './FuneralInfo'
import Member from './Member'
import { Confirm, DisplayItem } from '../generic';
import { ActivityIndicator } from '../generic'
import Beneficiary from './Beneficiary';
import EditBeneficiaryPercentages from './EditBeneficiaryPercentages';
import 'numeral/locales/en-za.js'

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
    justifyContent: 'flex-start',
    paddingBottom: 14,
  },
  table: {
    minWidth: 650,
    fontSize: 15,
  },
  head: {
    paddingTop: 7,
    paddingBottom: 7,
    fontWeight: 400,
  },
  cell: {
    paddingTop: 4,
    paddingBottom: 4,
    fontSize: 13,
    alignContent: 'flex-start'
  },
  button: {
    '&:hover': {
      backgroundColor: '#f00f15',
      color: '#fff',
    }
  },
  totalRow: {
    fontWeight: 600,
  }
});

export const BENEFICIARIES = gql`
  query Funeral($id: String!) {
    funeral(id: $id) {
      id
      beneficiaries {
        id
        payoutType
        percentage
        payToId
        payTo {
            id
            firstNames
            surname
            idNumber
            idType
            cellNumbers
            email
            relationship
          }
        bankAccount {
          id
          bankName
          accountNo
          associatedWith {
            id
            type
            reference
          }
        }
      }
    }
  }`

const UPDATE_FUNERAL = gql`
  mutation UpdateFuneralBeneficiaries($id: String!, $input: UpdateFuneralInput!) {
    updateFuneral(id: $id, input: $input) {
      id
      beneficiaries {
        id
        payoutType
        percentage
        payToId
        payTo {
          id
          firstNames
          surname
          idNumber
          idType
          cellNumbers
          email
          relationship
        }
        bankAccount {
          id
          bankName
          accountNo
        }
      }
    }
  }`

function Beneficiaries({ funeral, history, client }) {
  const classes = useStyles();
  const { data, loading } = useQuery(BENEFICIARIES, { variables: { id: funeral.id } }) || {}
  const [removing, setRemoving] = useState(null)

  if (loading) {
    return <ActivityIndicator />
  }

  const { funeral: funeralData } = data || {};
  const { beneficiaries } = funeralData || {};

  const validBeneficiaries = (beneficiaries || []).filter(b => b.payTo)
  const totalPercentage = validBeneficiaries.reduce((sum, b) => sum + (b.percentage || 0), 0)

  const rebalancePercentages = (items) => {
    if (items.length === 0) return items
    const total = items.reduce((sum, b) => sum + (b.percentage || 0), 0)
    const rebalanced = items.map(b => ({
      ...b,
      percentage: total > 0
        ? Math.round((b.percentage || 0) / total * 100 * 100) / 100
        : Math.round((100 / items.length) * 100) / 100,
    }))
    const roundedTotal = rebalanced.reduce((sum, b) => sum + b.percentage, 0)
    const diff = Math.round((100 - roundedTotal) * 100) / 100
    if (diff !== 0) {
      const last = rebalanced[rebalanced.length - 1]
      last.percentage = Math.round((last.percentage + diff) * 100) / 100
    }
    return rebalanced
  }

  const removeBeneficiary = (beneficiaryId) => {
    const remaining = rebalancePercentages(validBeneficiaries.filter(b => b.id !== beneficiaryId))
      .map(b => ({
        id: b.id,
        payToId: b.payToId,
        percentage: b.percentage,
        payoutType: b.payoutType,
        bankAccountId: b.bankAccount ? b.bankAccount.id : undefined,
      }))
    client.mutate({
      mutation: UPDATE_FUNERAL,
      variables: { id: funeral.id, input: { beneficiaries: remaining } },
      refetchQueries: [{ query: BENEFICIARIES, variables: { id: funeral.id } }],
    }).then(() => setRemoving(null))
  }

  return (
    <>
      <EditModal
        Content={Beneficiary}
        fontSize={18}
        fontWeight={400}
        noButton
        heading="Add Beneficiary"
        text="Beneficiaries"
        mode="add"
        id={funeral.id}
        existingBeneficiaries={validBeneficiaries}
        icon="add"
      />

      {validBeneficiaries.length === 0 ? (
        <div>No beneficiaries defined</div>
      ) : (
        <>
          {totalPercentage !== 100 && (
            <div style={{ color: '#c0392b', marginBottom: 8, fontSize: 13 }}>
              Warning: beneficiary percentages total {totalPercentage}% (must equal 100%)
            </div>
          )}
          <TableContainer size='small' component={Paper}>
            <Table className={classes.table} aria-label="beneficiaries table">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.head}>Name</TableCell>
                  <TableCell className={classes.head}>ID</TableCell>
                  <TableCell className={classes.head}>Relationship</TableCell>
                  <TableCell className={classes.head}>Mobile</TableCell>
                  <TableCell className={classes.head}>Email</TableCell>
                  <TableCell className={classes.head}>Bank Account</TableCell>
                  <TableCell className={classes.head}>%</TableCell>
                  <TableCell className={classes.head} />
                </TableRow>
              </TableHead>
              <TableBody>
                {validBeneficiaries.map((row) => {
                  const { id, payTo, bankAccount, percentage } = row
                  const { firstNames, surname, idNumber, cellNumbers, email, relationship } = payTo
                  const { bankName, accountNo } = bankAccount || {}
                  return (
                    <TableRow key={id}>
                      <TableCell className={classes.cell} component="th" scope="row">
                        <EditModal Content={Member} fontSize={13} fontWeight={400} noButton heading="Edit Beneficiary" size="xsmall" mode='edit' text={`${firstNames} ${surname}`} funeralId={funeral.id} id={payTo.id} />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {idNumber
                          ? history
                            ? <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => history.push(`/products/${idNumber}`)}>{idNumber}</span>
                            : idNumber
                          : ''}
                      </TableCell>
                      <TableCell className={classes.cell}>{relationship}</TableCell>
                      <TableCell className={classes.cell}>
                        {cellNumbers && cellNumbers[0]
                          ? <DisplayItem fontSize={13} borderBottomStyle='none' type='mobile' value={cellNumbers[0]} clientId={funeral.clientId} />
                          : ' '}
                      </TableCell>
                      <TableCell className={classes.cell}>{email}</TableCell>
                      <TableCell className={classes.cell}>{bankName} {accountNo}</TableCell>
                      <TableCell className={classes.cell}>{percentage != null ? `${percentage}%` : '-'}</TableCell>
                      <TableCell className={classes.cell}>
                        <IconButton
                          size="small"
                          aria-label="remove"
                          className={classes.button}
                          onClick={() => setRemoving(row)}
                        >
                          <RemoveCircleOutlineIcon style={{ color: '#D5D5D5', fontSize: 18 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
                <TableRow>
                  <TableCell colSpan={6} />
                  <TableCell className={classes.cell} style={{ fontWeight: 600 }}>
                    {totalPercentage}%
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <EditBeneficiaryPercentages
                      funeralId={funeral.id}
                      beneficiaries={validBeneficiaries}
                      refetchQueries={[{ query: BENEFICIARIES, variables: { id: funeral.id } }]}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Confirm
        open={!!removing}
        title="Remove beneficiary"
        description={removing ? `Remove ${removing.payTo.firstNames} ${removing.payTo.surname} as a beneficiary?` : ''}
        onOk={() => removeBeneficiary(removing.id)}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}

export default withApollo(Beneficiaries)
